/**
 * Subscription Management Models
 *
 * This module defines interfaces and types for managing organization subscriptions
 * and Stripe payment integration.
 *
 * NOTE: Subscriptions are organization-based, not user-based.
 * Each organization has one subscription that covers all users.
 */

export interface SubscriptionPlan {
	id: string;
	name: string;
	description: string;
	price: number;
	currency: string;
	interval: "month" | "year";
	features: string[];
	maxUsers?: number;
	maxLocations?: number;
	maxStorage?: number;
	isPopular?: boolean;
	stripeProductId?: string;
	stripePriceId?: string;
}

export interface UserSubscription {
	id: string;
	organizationId: number;
	planId: string;
	status: SubscriptionStatus;
	currentPeriodStart: string;
	currentPeriodEnd: string;
	cancelAtPeriodEnd: boolean;
	stripeSubscriptionId?: string;
	stripeCustomerId?: string;
	createdAt: string;
	updatedAt: string;
	// Deprecated fields (kept for backward compatibility)
	userId?: number;
}

export enum SubscriptionStatus {
	ACTIVE = "active",
	CANCELED = "canceled",
	INCOMPLETE = "incomplete",
	INCOMPLETE_EXPIRED = "incomplete_expired",
	PAST_DUE = "past_due",
	TRIALING = "trialing",
	UNPAID = "unpaid",
}

export interface PaymentMethod {
	id: number;
	organizationId: number;
	stripePaymentMethodId: string;
	type: "card";
	card: {
		brand: string;
		last4: string;
		expMonth: number;
		expYear: number;
	};
	cardBrand?: string;
	cardLast4?: string;
	cardExpMonth?: number;
	cardExpYear?: number;
	isDefault: boolean;
	createdAt: string;
	updatedAt: string;
	// Deprecated fields (kept for backward compatibility)
	userId?: number;
}

export interface Invoice {
	id: string;
	stripeInvoiceId: string;
	invoiceNumber: string;
	status: "draft" | "open" | "paid" | "uncollectible" | "void" | "unknown";
	amountPaid: number;
	amountDue: number;
	currency: string;
	invoiceDate: string;
	dueDate?: string | null;
	paidAt?: string | null;
	hostedInvoiceUrl?: string | null;
	invoicePdf?: string | null;
	// Additional Stripe fields for richer display
	description?: string;
	subtotal?: number;
	tax?: number;
	total?: number;
	// Payment method information
	paymentMethod?: {
		type: string;
		last4?: string;
		brand?: string;
	} | null;
	// Subscription information
	subscriptionId?: string;
	// Detailed line items
	lineItems?: {
		description: string;
		amount: number;
		quantity: number;
		unitAmount: number;
		period?: {
			start: string;
			end: string;
		} | null;
	}[];
}

export interface SubscriptionUsage {
	// CamelCase format (preferred)
	currentUsers: number;
	maxUsers: number;
	currentLocations: number;
	maxLocations: number;
	storageUsed: number;
	storageLimit: number;
	// Snake_case format (for backward compatibility)
	current_users?: number;
	current_locations?: number;
	current_storage?: number;
	maxStorage?: number;
	subscriptionId?: number;
	subscription_id?: number;
	calculatedAt?: string;
	calculated_at?: string;
}

export interface BillingAddress {
	line1: string;
	line2?: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
}

export interface SubscriptionChangePreview {
	currentPlan: SubscriptionPlan;
	newPlan: SubscriptionPlan;
	proratedAmount: number;
	nextBillingDate: string;
	immediateCharge: number;
}
